import { Box, Button, TextField, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { GoogleLoginButton } from 'infrastructure/googleDrive'
import { parseSheetIdOrUrl } from 'shared/utils/googleSheets'
import {
  currentSheetRawDataAtom,
  currentSpreadsheetIdAtom,
  sheetDataForTable,
} from 'domains/scope-and-sequence/features/parser/atoms'
import { Column } from 'shared/ui/Table/types'
import { ScopeAndSequenceRow } from 'domains/scope-and-sequence/features/parser/types'
import { Table } from 'shared/ui/Table/Table'
import { alternatingRowColor } from './utility'
import { snakeKabobToTitleCase } from 'shared/utils'
import { CreateTemplateButton } from './CreateTemplateButton'
import {
  useGetTabValues,
  useListTabs,
} from 'components/tree/googleApis/spreadsheets/hooks'
import { SheetTab } from 'infrastructure/googleDrive'
import { globalDialogAtom } from 'shared/ui/GlobalDialog'

export function useImportSpreadsheet() {
  const [loading, setLoading] = useState(false)
  const setSheetData = useSetAtom(currentSheetRawDataAtom)
  const setSpreadsheetId = useSetAtom(currentSpreadsheetIdAtom)
  const setDialog = useSetAtom(globalDialogAtom)
  const listTabs = useListTabs()
  const getTabValues = useGetTabValues()

  const importSpreadsheet = useCallback(
    async (spreadsheetId: string) => {
      if (!spreadsheetId) return

      setSpreadsheetId(spreadsheetId)
      setLoading(true)
      try {
        const tabs = await listTabs(spreadsheetId)
        if (!tabs || tabs.length === 0) {
          setSheetData(undefined)
          return
        }

        const firstTab = tabs[0] as SheetTab
        const data = await getTabValues({
          spreadsheetId,
          title: firstTab.title,
        })
        setSheetData(data?.values)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred'
        setDialog({
          title: 'Failed to Import Spreadsheet',
          contents: [errorMessage],
        })
        setSheetData(undefined)
      } finally {
        setLoading(false)
      }
    },
    [listTabs, getTabValues, setSheetData, setSpreadsheetId, setDialog],
  )

  return { importSpreadsheet, loading }
}

const HELPER_TEXT =
  'Use the template provided to structure your pacing guide. The template includes 2 example rows showing the correct format. If a unit covers multiple standards, add each standard on a separate row. Ensure your spreadsheet includes the required headers: Grade, Unit Number, Unit Name, Standard.'

export function SSDetails() {
  return (
    <Box>
      <GoogleLoginButton
        ParentComponent={LoginPrompt}
        LoggedInComponent={LoggedInContent}
      />
    </Box>
  )
}

function LoginPrompt({ children }: { children: React.ReactNode }) {
  return (
    <Box padding="10px">
      <Box>Google Auth Required To Access Spreadsheets</Box>
      {children}
    </Box>
  )
}

function LoggedInContent() {
  const { importSpreadsheet, loading } = useImportSpreadsheet()

  return (
    <Box padding="10px" paddingTop="0px">
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: '800px' }}
      >
        {HELPER_TEXT}
      </Typography>
      <CreateTemplateButton onTemplateCreated={importSpreadsheet} />
      <Box sx={{ mt: 3 }}>
        <SpreadsheetImporter
          importSpreadsheet={importSpreadsheet}
          loading={loading}
        />
      </Box>
      <ParsedTable />
    </Box>
  )
}

function SpreadsheetImporter({
  importSpreadsheet,
  loading,
}: {
  importSpreadsheet: (spreadsheetId: string) => Promise<void>
  loading: boolean
}) {
  const [storedSpreadsheetId, setStoredSpreadsheetId] = useAtom(
    currentSpreadsheetIdAtom,
  )
  const [inputValue, setInputValue] = useState(storedSpreadsheetId ?? '')

  // Derive the spreadsheet ID from input (handles both URLs and raw IDs)
  const { spreadsheetId } = parseSheetIdOrUrl(inputValue)

  // Sync input when stored ID changes (e.g., from template creation)
  React.useEffect(() => {
    if (storedSpreadsheetId && storedSpreadsheetId !== spreadsheetId) {
      setInputValue(storedSpreadsheetId)
    }
  }, [storedSpreadsheetId, spreadsheetId])

  const handleImport = () => {
    if (spreadsheetId) {
      importSpreadsheet(spreadsheetId)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    // Update the atom with the parsed ID
    const { spreadsheetId: parsedId } = parseSheetIdOrUrl(newValue)
    if (parsedId) {
      setStoredSpreadsheetId(parsedId)
    }
  }

  return (
    <Box>
      <TextField
        label="Paste Spreadsheet URL or ID"
        fullWidth
        value={inputValue}
        onChange={handleInputChange}
        size="small"
        sx={{ maxWidth: '500px', mb: 1 }}
      />
      <Box>
        <Button
          variant="outlined"
          disabled={!spreadsheetId || loading}
          onClick={handleImport}
        >
          {loading ? 'Importing...' : 'Import'}
        </Button>
      </Box>
    </Box>
  )
}

function ParsedTable() {
  const parsed = useAtomValue(sheetDataForTable)
  if (!parsed?.length) return null

  const columns = (
    [
      'grade',
      'unitNumber',
      'unitName',
      'standard',
    ] as (keyof ScopeAndSequenceRow)[]
  ).map<Column<ScopeAndSequenceRow>>((key) => {
    return {
      title: snakeKabobToTitleCase(key),
      width: ['grade', 'unitNumber'].includes(key) ? 50 : undefined,
      rowContent(row: ScopeAndSequenceRow) {
        return row[key]
      },
    }
  })

  return (
    <Box sx={{ mt: 2 }}>
      <Table
        title="Scope & Sequence"
        columns={columns}
        items={parsed}
        getRowStyle={(idx) => {
          const unitNum = parsed[idx].unitNumber
          return alternatingRowColor(unitNum)
        }}
      />
    </Box>
  )
}
